import {
  ComponentProps,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  Ref,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  ClickAwayListener,
  Paper,
  Popper,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core/styles';
import { alpha } from '@mui/material/styles';
import {
  SidebarOpenStateProvider,
  useSidebarOpenState,
} from '@backstage/core-components';

type NavigationPalette = {
  color?: string;
  indicator?: string;
  navItem?: { hoverBackground?: string };
  submenu?: { background?: string };
};

export const getSidebarFlyoutPalette = (theme: Theme) => {
  const navigation =
    (theme.palette as { navigation?: NavigationPalette }).navigation ?? {};
  const isLight = theme.palette.type === 'light';
  const hover = isLight
    ? alpha(theme.palette.primary.main, 0.08)
    : (navigation.navItem?.hoverBackground ?? 'rgba(255, 255, 255, 0.1)');

  return {
    color: isLight
      ? theme.palette.text.primary
      : (navigation.color ?? theme.palette.common.white),
    continuation: isLight
      ? 'rgb(105, 66, 156)'
      : (navigation.indicator ?? hover),
    hover,
    isLight,
    surface: isLight
      ? theme.palette.background.default
      : (navigation.submenu?.background ?? theme.palette.background.paper),
  };
};

export const useSidebarHoverFlyoutStyles = makeStyles(theme => {
  const { color, continuation, isLight, surface } =
    getSidebarFlyoutPalette(theme);

  return {
    paper: {
      pointerEvents: 'auto' as const,
      color,
      backgroundColor: surface,
      borderRadius: '0 10px 10px 0',
      borderTop: `2px solid ${continuation}`,
      borderRight: `2px solid ${continuation}`,
      borderBottom: `2px solid ${continuation}`,
      borderLeft: 0,
      boxShadow: isLight
        ? '8px 0 16px -8px rgba(0, 0, 0, 0.24), 0 8px 16px -8px rgba(0, 0, 0, 0.24)'
        : '8px 0 20px -8px rgba(0, 0, 0, 0.45), 0 10px 20px -10px rgba(0, 0, 0, 0.45)',
      transition: 'none',
    },
  };
});

type PopperInstance =
  NonNullable<ComponentProps<typeof Popper>['popperRef']> extends Ref<
    infer Instance
  >
    ? Instance
    : never;

type UseHoverFlyoutOptions = {
  open: boolean;
  menuId: string;
  onOpen: () => void;
  onClose: () => void;
  enabled?: boolean;
  triggerSelector?: string;
  closeDelayMs?: number;
};

export const useHoverFlyout = ({
  open,
  menuId,
  onOpen,
  onClose,
  enabled = true,
  triggerSelector = 'a, button',
  closeDelayMs = 150,
}: UseHoverFlyoutOptions) => {
  const theme = useTheme();
  const { isOpen: sidebarOpen } = useSidebarOpenState();
  const anchorRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<PopperInstance | null>(null);
  const closeTimerRef = useRef<number>();

  const getTrigger = useCallback(
    () => anchorRef.current?.querySelector<HTMLElement>(triggerSelector),
    [triggerSelector],
  );

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);

  const close = useCallback(
    (restoreFocus = false) => {
      clearCloseTimer();
      onClose();
      if (restoreFocus) {
        getTrigger()?.focus();
      }
    },
    [clearCloseTimer, getTrigger, onClose],
  );

  const show = useCallback(
    (focusFirstItem = false) => {
      if (!enabled) {
        return;
      }
      clearCloseTimer();
      onOpen();
      if (focusFirstItem) {
        requestAnimationFrame(() => {
          paperRef.current
            ?.querySelector<HTMLElement>('[role^="menuitem"]')
            ?.focus();
        });
      }
    },
    [clearCloseTimer, enabled, onOpen],
  );

  const closeAfterDelay = useCallback(() => {
    if (!open) {
      return;
    }
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      close();
    }, closeDelayMs);
  }, [clearCloseTimer, close, closeDelayMs, open]);

  const containsTarget = useCallback(
    (target: EventTarget | null) =>
      target instanceof Node &&
      (anchorRef.current?.contains(target) ||
        paperRef.current?.contains(target)),
    [],
  );

  const handleMouseLeave = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (containsTarget(event.relatedTarget)) {
        return;
      }
      if (
        event.relatedTarget instanceof Element &&
        event.relatedTarget.closest('a, button')
      ) {
        close();
        return;
      }
      closeAfterDelay();
    },
    [close, closeAfterDelay, containsTarget],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (!containsTarget(event.relatedTarget)) {
        close();
      }
    },
    [close, containsTarget],
  );

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (
        !(event.target instanceof Node) ||
        !anchorRef.current?.contains(event.target)
      ) {
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        show(true);
      } else if (event.key === 'Escape' && open) {
        event.preventDefault();
        close(true);
      }
    },
    [close, open, show],
  );

  const handlePanelKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Escape') {
        return false;
      }
      event.preventDefault();
      close(true);
      return true;
    },
    [close],
  );

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    [clearCloseTimer],
  );

  useEffect(() => {
    const trigger = getTrigger();
    if (!trigger || !enabled) {
      return;
    }
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      trigger.setAttribute('aria-controls', menuId);
    } else {
      trigger.removeAttribute('aria-controls');
    }
  }, [enabled, getTrigger, menuId, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const updatePosition = () => popperRef.current?.scheduleUpdate();
    updatePosition();
    const timer = window.setTimeout(
      updatePosition,
      theme.transitions.duration.shorter,
    );
    return () => window.clearTimeout(timer);
  }, [open, sidebarOpen, theme.transitions.duration.shorter]);

  return {
    anchorRef,
    paperRef,
    popperRef,
    open,
    show,
    close,
    clearCloseTimer,
    handleBlur,
    handleMouseLeave,
    handlePanelKeyDown,
    handleTriggerKeyDown,
  };
};

type SidebarHoverFlyoutPanelProps = PropsWithChildren<{
  controller: ReturnType<typeof useHoverFlyout>;
  placement: ComponentProps<typeof Popper>['placement'];
  className: string;
}>;

export const SidebarHoverFlyoutPanel = ({
  children,
  className,
  controller,
  placement,
}: SidebarHoverFlyoutPanelProps) => (
  <Popper
    role="presentation"
    open={controller.open}
    anchorEl={controller.anchorRef.current}
    placement={placement}
    popperRef={controller.popperRef}
  >
    <ClickAwayListener onClickAway={() => controller.close()}>
      <Paper
        ref={controller.paperRef}
        className={className}
        onMouseEnter={controller.clearCloseTimer}
        onMouseLeave={controller.handleMouseLeave}
        onBlur={controller.handleBlur}
      >
        <SidebarOpenStateProvider value={{ isOpen: true, setOpen: () => {} }}>
          {children}
        </SidebarOpenStateProvider>
      </Paper>
    </ClickAwayListener>
  </Popper>
);
